/**
 * @fileoverview Email Template Management React Query Hooks
 *
 * Provides React Query hooks for email template CRUD operations.
 * Integrates with EmailTemplateService for backend communication and handles
 * automatic cache invalidation.
 *
 * @module hooks/useEmailTemplates
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@xanectech/ui/lib';
import {
  EmailTemplateService,
  type EmailTemplate,
  type CreateEmailTemplateData,
  type UpdateEmailTemplateData,
} from '../services/EmailTemplateService';
import { createLogger } from '@xanectech/lib';

const logger = createLogger('Data - useEmailTemplates');

/**
 * Hook to fetch all email templates
 *
 * Retrieves the complete list of email templates in the system.
 *
 * @returns {UseQueryResult<EmailTemplate[]>} React Query result with email templates data
 *
 * @example
 * ```tsx
 * function EmailTemplateList() {
 *   const { data: templates, isLoading, error } = useEmailTemplates();
 *
 *   if (isLoading) return <LoadingSpinner />;
 *   if (error) return <ErrorMessage error={error} />;
 *
 *   return (
 *     <ul>
 *       {templates?.map(template => (
 *         <li key={template.uuid}>{template.name}</li>
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 */
export const useEmailTemplates = () => {
  return useQuery({
    queryKey: ['email-templates'],
    queryFn: EmailTemplateService.getAllEmailTemplates,
  });
};

/**
 * Hook to fetch active email templates only
 *
 * @returns {UseQueryResult<EmailTemplate[]>} React Query result with active templates
 *
 * @example
 * ```tsx
 * function ActiveTemplateSelector() {
 *   const { data: templates } = useActiveEmailTemplates();
 *
 *   return (
 *     <select>
 *       {templates?.map(template => (
 *         <option key={template.uuid} value={template.uuid}>
 *           {template.name}
 *         </option>
 *       ))}
 *     </select>
 *   );
 * }
 * ```
 */
export const useActiveEmailTemplates = () => {
  return useQuery({
    queryKey: ['email-templates', 'active'],
    queryFn: EmailTemplateService.getActiveEmailTemplates,
  });
};

/**
 * Hook to fetch email template statistics
 *
 * Retrieves aggregated statistics about email templates
 * (total count, active, inactive)
 *
 * @returns {UseQueryResult} React Query result with template statistics
 *
 * @example
 * ```tsx
 * function TemplateDashboard() {
 *   const { data: stats } = useEmailTemplateStats();
 *
 *   return (
 *     <div>
 *       <h2>Total Templates: {stats?.total}</h2>
 *       <h2>Active: {stats?.active}</h2>
 *       <h2>Inactive: {stats?.inactive}</h2>
 *     </div>
 *   );
 * }
 * ```
 */
export const useEmailTemplateStats = () => {
  return useQuery({
    queryKey: ['email-template-stats'],
    queryFn: EmailTemplateService.getEmailTemplateStats,
  });
};

/**
 * Hook to fetch a single email template by UUID
 *
 * Retrieves detailed information for a specific email template.
 * Query is only executed when uuid is provided.
 *
 * @param {string} [uuid] - The unique identifier of the email template
 * @returns {UseQueryResult<EmailTemplate>} React Query result with template data
 *
 * @example
 * ```tsx
 * function EmailTemplateDetails({ id }: { id: string }) {
 *   const { data: template, isLoading } = useEmailTemplate(id);
 *
 *   if (isLoading) return <LoadingSpinner />;
 *
 *   return (
 *     <div>
 *       <h1>{template?.name}</h1>
 *       <p>{template?.subject}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export const useEmailTemplate = (uuid?: string) => {
  return useQuery({
    queryKey: ['email-template', uuid],
    queryFn: () => {
      if (!uuid) throw new Error('Email template UUID is required');
      return EmailTemplateService.getEmailTemplate(uuid);
    },
    enabled: !!uuid,
  });
};

/**
 * Hook to fetch email template by Mailtrap template_id
 *
 * @param {string} [template_id] - The Mailtrap template ID
 * @returns {UseQueryResult<EmailTemplate | null>} React Query result with template data
 */
export const useEmailTemplateByMailtrapId = (template_id?: string) => {
  return useQuery({
    queryKey: ['email-template', 'mailtrap', template_id],
    queryFn: () => {
      if (!template_id) throw new Error('Mailtrap template ID is required');
      return EmailTemplateService.getEmailTemplateByMailtrapId(template_id);
    },
    enabled: !!template_id,
  });
};

/**
 * Hook to search email templates by name
 *
 * @param {Object} options - Search options
 * @param {string} options.query - The search query string
 * @param {number} [options.limit=20] - Maximum number of results to return
 * @param {boolean} [options.enabled=true] - Whether the query should be enabled
 * @returns {UseQueryResult<EmailTemplate[]>} React Query result with matching templates
 *
 * @example
 * ```tsx
 * function TemplateSearch() {
 *   const [searchTerm, setSearchTerm] = useState('');
 *   const { data: templates, isLoading } = useEmailTemplateSearch({
 *     query: searchTerm,
 *     limit: 10
 *   });
 *
 *   return (
 *     <div>
 *       <input
 *         value={searchTerm}
 *         onChange={(e) => setSearchTerm(e.target.value)}
 *       />
 *       {templates?.map(template => (
 *         <div key={template.uuid}>{template.name}</div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const useEmailTemplateSearch = ({
  query,
  limit = 20,
  enabled = true,
}: {
  query: string;
  limit?: number;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ['email-templates-search', query, limit],
    queryFn: () => EmailTemplateService.searchEmailTemplates(query, limit),
    enabled: enabled && query.trim().length > 0,
  });
};

/**
 * Hook to create a new email template
 *
 * Mutation hook for creating a new email template record.
 * Automatically invalidates template-related queries and shows success/error toasts.
 *
 * @returns {UseMutationResult} React Query mutation for creating templates
 *
 * @example
 * ```tsx
 * function CreateTemplateForm() {
 *   const createTemplate = useCreateEmailTemplate();
 *
 *   const handleSubmit = async (formData) => {
 *     await createTemplate.mutateAsync({
 *       template_id: formData.template_id,
 *       name: formData.name,
 *       subject: formData.subject,
 *       template_variables: formData.variables
 *     });
 *   };
 *
 *   return (form with fields);
 * }
 * ```
 */
export const useCreateEmailTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: EmailTemplateService.createEmailTemplate,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      void queryClient.invalidateQueries({
        queryKey: ['email-template-stats'],
      });
      toast({
        title: 'Template Created',
        description: 'New email template has been successfully created.',
      });
    },
    onError: (error) => {
      logger.error('Failed to create email template', { error });
      toast({
        title: 'Error',
        description: `Failed to create template: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook to update an email template
 *
 * Mutation hook for updating an existing email template.
 * Automatically invalidates template-related queries and shows success/error toasts.
 *
 * @returns {UseMutationResult} React Query mutation for updating templates
 */
export const useUpdateEmailTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      uuid,
      updates,
    }: {
      uuid: string;
      updates: UpdateEmailTemplateData;
    }) => EmailTemplateService.updateEmailTemplate(uuid, updates),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      void queryClient.invalidateQueries({
        queryKey: ['email-template', variables.uuid],
      });
      void queryClient.invalidateQueries({
        queryKey: ['email-template-stats'],
      });
      toast({
        title: 'Template Updated',
        description: 'Email template has been successfully updated.',
      });
    },
    onError: (error) => {
      logger.error('Failed to update email template', { error });
      toast({
        title: 'Error',
        description: `Failed to update template: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook to toggle email template active status
 *
 * @returns {UseMutationResult} React Query mutation for toggling template status
 */
export const useToggleEmailTemplateActive = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uuid, is_active }: { uuid: string; is_active: boolean }) =>
      EmailTemplateService.toggleEmailTemplateActive(uuid, is_active),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      void queryClient.invalidateQueries({
        queryKey: ['email-template', variables.uuid],
      });
      void queryClient.invalidateQueries({
        queryKey: ['email-template-stats'],
      });
      toast({
        title: 'Status Updated',
        description: `Template ${variables.is_active ? 'activated' : 'deactivated'} successfully.`,
      });
    },
    onError: (error) => {
      logger.error('Failed to toggle template status', { error });
      toast({
        title: 'Error',
        description: `Failed to update status: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook to delete an email template
 *
 * Mutation hook for deleting an email template.
 * Automatically invalidates template-related queries and shows success/error toasts.
 *
 * @returns {UseMutationResult} React Query mutation for deleting templates
 */
export const useDeleteEmailTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: EmailTemplateService.deleteEmailTemplate,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      void queryClient.invalidateQueries({
        queryKey: ['email-template-stats'],
      });
      toast({
        title: 'Template Deleted',
        description: 'Email template has been successfully deleted.',
      });
    },
    onError: (error) => {
      logger.error('Failed to delete email template', { error });
      toast({
        title: 'Error',
        description: `Failed to delete template: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook to sync all Mailtrap templates into the local email_templates table.
 *
 * Fetches all templates from the Mailtrap management API and upserts them into
 * the database keyed on `template_id`. Invalidates template-related queries on
 * success and shows appropriate toast notifications.
 *
 * @returns {UseMutationResult} React Query mutation for syncing templates from Mailtrap
 *
 * @example
 * ```tsx
 * function SyncButton() {
 *   const { mutate: syncTemplates, isPending } = useSyncMailtrapTemplates();
 *
 *   return (
 *     <Button onClick={() => syncTemplates()} disabled={isPending}>
 *       {isPending ? 'Syncing…' : 'Get Templates'}
 *     </Button>
 *   );
 * }
 * ```
 */
export const useSyncMailtrapTemplates = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: EmailTemplateService.syncMailtrapTemplates,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      void queryClient.invalidateQueries({
        queryKey: ['email-template-stats'],
      });
      toast({
        title: 'Templates Synced',
        description: 'Email templates synced successfully from Mailtrap.',
      });
    },
    onError: (error) => {
      logger.error('Failed to sync templates from Mailtrap', { error });
      toast({
        title: 'Error',
        description: `Failed to sync templates: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
};
